import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSalesService } from 'services/sales';
import { API_ROUTES, ROUTES } from 'utils/constants';
import { Company } from 'types/company';
import { useApiClient } from 'hooks/useApiClient';
import {
  Customer,
  PaymentMethod,
  Product as SalesProduct,
  ProductEntry,
  CashOrArEntry,
} from 'types/sales';
import DatePicker from 'components/DatePicker';
import Toast from 'components/Toast';
import Button from 'components/Button';
import ProductSearchInput from 'components/ProductSearchInput';
import CustomerSearchInput from 'components/CustomerSearchInput';
import SearchInput from 'components/SearchInput';
import styles from './SalesTransaction.module.scss';

interface ProductEntryState {
  id: string;
  productId: string;
  quantity: number | string | undefined;
}

interface CashOrArEntryState {
  id: string;
  categoryId: string;
  amount: number | string | undefined;
}

type SalesFormData = {
  description: string;
  date: string;
  customerId: string;
  productEntries: ProductEntry[];
  cashOrArEntries: CashOrArEntry[];
};

function SalesTransaction() {
  const navigate = useNavigate();
  const salesService = useSalesService();
  const apiClient = useApiClient();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerId, setCustomerId] = useState('');
  const [productEntries, setProductEntries] = useState<ProductEntryState[]>([
    { id: '1', productId: '', quantity: undefined },
  ]);
  const [cashOrArEntries, setCashOrArEntries] = useState<CashOrArEntryState[]>([
    { id: '1', categoryId: '', amount: undefined },
  ]);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [products, setProducts] = useState<SalesProduct[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<SalesFormData>({
    defaultValues: {
      description: '',
      date: date,
      customerId: '',
      productEntries: [],
      cashOrArEntries: [],
    },
  });

  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Load sales options and company
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const options = await salesService.getSalesOptions();

        // Check if all prerequisites are met
        const hasCustomers = options.customers && options.customers.length > 0;
        const hasPaymentMethods =
          options.paymentMethods && options.paymentMethods.length > 0;
        const hasProducts = options.products && options.products.length > 0;

        if (!hasCustomers || !hasPaymentMethods || !hasProducts) {
          // Redirect to sales page if prerequisites are not met
          navigate(ROUTES.SALES.ROOT);
          return;
        }

        let companyData: Company | null = null;
        if (apiClient.companyId) {
          companyData = await apiClient.get<Company>(
            `${API_ROUTES.COMPANY}/${apiClient.companyId}`,
          );
        }
        setCustomers(options.customers);
        setPaymentMethods(options.paymentMethods);
        setProducts(options.products);
        setCompany(companyData);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load data. Please try again.';
        showToast(errorMessage, 'error');
        // Redirect to sales page on error
        navigate(ROUTES.SALES.ROOT);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [salesService, apiClient, showToast, navigate]);

  // Calculate total revenue from products
  const totalRevenue = productEntries.reduce((sum, entry) => {
    if (!entry.productId || !entry.quantity) return sum;
    const product = products.find((p) => p.id === entry.productId);
    if (!product) return sum;
    const quantity =
      typeof entry.quantity === 'string'
        ? Number(entry.quantity) || 0
        : entry.quantity || 0;
    return sum + product.price * quantity;
  }, 0);

  // Auto-compute Cash/AR amount when there's only one entry
  useEffect(() => {
    if (cashOrArEntries.length === 1 && totalRevenue > 0) {
      const currentAmount = cashOrArEntries[0]?.amount;
      if (currentAmount === undefined) {
        setCashOrArEntries((prev) => [
          {
            ...prev[0],
            amount: totalRevenue,
          },
        ]);
      }
    }
  }, [totalRevenue, cashOrArEntries.length, cashOrArEntries]);

  // Calculate total cash/AR
  const totalCashOrAr =
    cashOrArEntries.length === 1
      ? totalRevenue
      : cashOrArEntries.reduce((sum, entry) => {
          const amount =
            typeof entry.amount === 'string'
              ? Number(entry.amount) || 0
              : entry.amount || 0;
          return sum + amount;
        }, 0);

  const balanceDifference = Math.abs(totalRevenue - totalCashOrAr);
  const needsSplit = cashOrArEntries.length > 1;

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  // Product entry handlers
  const handleAddProductEntry = () => {
    setProductEntries([
      ...productEntries,
      { id: Date.now().toString(), productId: '', quantity: undefined },
    ]);
  };

  const handleRemoveProductEntry = (id: string) => {
    if (productEntries.length > 1) {
      setProductEntries(productEntries.filter((entry) => entry.id !== id));
    }
  };

  const handleProductEntryChange = useCallback(
    (
      id: string,
      field: keyof ProductEntryState,
      value: string | number | undefined,
    ) => {
      setProductEntries((prevEntries) =>
        prevEntries.map((entry) => {
          if (entry.id === id) {
            if (field === 'quantity') {
              if (value === '' || value === undefined) {
                return { ...entry, [field]: undefined };
              }
              if (typeof value === 'string' && value.endsWith('.')) {
                return { ...entry, [field]: value };
              }
              const numValue =
                typeof value === 'string' ? Number(value) : value;
              return {
                ...entry,
                [field]: isNaN(numValue) ? undefined : numValue,
              };
            }
            return { ...entry, [field]: value };
          }
          return entry;
        }),
      );
    },
    [],
  );

  // Cash/AR entry handlers
  const handleAddCashOrArEntry = () => {
    if (cashOrArEntries.length === 1) {
      setCashOrArEntries([
        { ...cashOrArEntries[0], amount: undefined },
        { id: Date.now().toString(), categoryId: '', amount: undefined },
      ]);
    } else {
      setCashOrArEntries([
        ...cashOrArEntries,
        { id: Date.now().toString(), categoryId: '', amount: undefined },
      ]);
    }
  };

  const handleRemoveCashOrArEntry = (id: string) => {
    if (cashOrArEntries.length > 1) {
      const remainingEntries = cashOrArEntries.filter(
        (entry) => entry.id !== id,
      );
      if (remainingEntries.length === 1 && totalRevenue > 0) {
        setCashOrArEntries([
          {
            ...remainingEntries[0],
            amount: totalRevenue,
          },
        ]);
      } else {
        setCashOrArEntries(remainingEntries);
      }
    }
  };

  const handleCashOrArEntryChange = useCallback(
    (
      id: string,
      field: keyof CashOrArEntryState,
      value: string | number | undefined,
    ) => {
      setCashOrArEntries((prevEntries) =>
        prevEntries.map((entry) => {
          if (entry.id === id) {
            if (field === 'amount') {
              if (value === '' || value === undefined) {
                return { ...entry, [field]: undefined };
              }
              if (typeof value === 'string' && value.endsWith('.')) {
                return { ...entry, [field]: value };
              }
              const numValue =
                typeof value === 'string' ? Number(value) : value;
              return {
                ...entry,
                [field]: isNaN(numValue) ? undefined : numValue,
              };
            }
            return { ...entry, [field]: value };
          }
          return entry;
        }),
      );
    },
    [],
  );

  // Form submission
  const handleOnSubmit: SubmitHandler<SalesFormData> = (
    data: SalesFormData,
  ) => {
    if (balanceDifference > 0.01) {
      showToast(
        'Total revenue amount must equal total cash/accounts receivable amount',
        'error',
      );
      return;
    }

    const requestData = {
      date: data.date,
      customerId: customerId,
      productEntries: productEntries
        .filter((entry) => entry.productId && entry.quantity)
        .map((entry) => ({
          productId: entry.productId,
          quantity:
            typeof entry.quantity === 'string'
              ? Number(entry.quantity)
              : entry.quantity || 0,
        })),
      cashOrArEntries: cashOrArEntries
        .filter((entry) => entry.categoryId)
        .map((entry) => ({
          categoryId: entry.categoryId,
          amount:
            cashOrArEntries.length === 1
              ? totalRevenue
              : typeof entry.amount === 'string'
                ? Number(entry.amount) || 0
                : entry.amount || 0,
        })),
      ...(data.description && { description: data.description }),
    };

    void (async () => {
      setIsSubmitting(true);
      try {
        await salesService.createSales(requestData);
        showToast('Sales transaction created successfully', 'success');

        setTimeout(() => {
          navigate(ROUTES.SALES.ROOT);
        }, 1500);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        showToast(errorMessage, 'error');
      } finally {
        setIsSubmitting(false);
      }
    })();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <button
          className={styles.backButton}
          onClick={() => navigate(ROUTES.SALES.ROOT)}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      <div className={styles.invoiceContainer}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(handleOnSubmit)(e);
          }}
        >
          {/* Invoice Header */}
          <div className={styles.invoiceHeader}>
            <div className={styles.companyInfo}>
              <h1 className={styles.companyName}>
                {company?.name || 'Company Name'}
              </h1>
            </div>
            <div className={styles.invoiceInfo}>
              <h2 className={styles.invoiceTitle}>NEW SALES INVOICE</h2>
              <div className={styles.invoiceMeta}>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Date:</span>
                  <div className={styles.metaValue}>
                    <input
                      type="hidden"
                      {...register('date', {
                        required: 'Date is required',
                      })}
                    />
                    <DatePicker
                      value={date}
                      onChange={(newDate) => {
                        setDate(newDate);
                        setValue('date', newDate, { shouldValidate: true });
                      }}
                      placeholder="Select date"
                    />
                    {errors.date && (
                      <p className={styles.errorMessage}>
                        {errors.date.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Customer:</span>
                  <div className={styles.metaValue}>
                    <input
                      type="hidden"
                      {...register('customerId', {
                        required: 'Customer is required',
                      })}
                    />
                    <CustomerSearchInput
                      customers={customers}
                      value={customerId}
                      onChange={(id) => {
                        setCustomerId(id);
                        setValue('customerId', id, { shouldValidate: true });
                      }}
                      placeholder="Search for a customer..."
                      required
                      disabled={isSubmitting}
                    />
                    {errors.customerId && (
                      <p className={styles.errorMessage}>
                        {errors.customerId.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          {totalCashOrAr > 0 && (
            <div className={styles.paymentSummary}>
              <p className={styles.paymentSummaryText}>
                ${formatCurrency(totalCashOrAr)} paid on {formatDate(date)}
              </p>
            </div>
          )}

          {/* Products Table */}
          <div className={styles.itemsSection}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Products / Services</h3>
            </div>
            <table className={styles.itemsTable}>
              <thead>
                <tr>
                  <th className={styles.itemHeader}>#</th>
                  <th className={styles.itemHeader}>Product</th>
                  <th className={styles.itemHeaderAmount}>Quantity</th>
                  <th className={styles.itemHeaderAmount}>Price</th>
                  <th className={styles.itemHeaderAmount}>Total</th>
                  <th className={styles.itemHeaderActions}></th>
                </tr>
              </thead>
              <tbody>
                {productEntries.map((entry, index) => {
                  const product = products.find(
                    (p) => p.id === entry.productId,
                  );
                  const quantity =
                    typeof entry.quantity === 'string'
                      ? Number(entry.quantity) || 0
                      : entry.quantity || 0;
                  const price = product ? product.price : 0;
                  const total = price * quantity;

                  return (
                    <tr key={entry.id} className={styles.itemRow}>
                      <td className={styles.itemNumber}>{index + 1}</td>
                      <td className={styles.itemDescription}>
                        <ProductSearchInput
                          products={products}
                          value={entry.productId}
                          onChange={(productId) => {
                            handleProductEntryChange(
                              entry.id,
                              'productId',
                              productId,
                            );
                          }}
                          placeholder="Search for a product..."
                          required
                          disabled={isSubmitting}
                          excludeProductIds={productEntries
                            .filter((e) => e.id !== entry.id)
                            .map((e) => e.productId)}
                        />
                      </td>
                      <td className={styles.itemAmount}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={
                            entry.quantity !== undefined &&
                            entry.quantity !== null
                              ? typeof entry.quantity === 'string'
                                ? entry.quantity
                                : entry.quantity.toString()
                              : ''
                          }
                          placeholder="0.00"
                          className={styles.numberInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                              handleProductEntryChange(
                                entry.id,
                                'quantity',
                                value === '' ? undefined : value,
                              );
                            }
                          }}
                          disabled={isSubmitting || !entry.productId}
                        />
                      </td>
                      <td className={styles.itemAmount}>
                        {product ? `$${formatCurrency(price)}` : '-'}
                      </td>
                      <td className={styles.itemAmount}>
                        {product && quantity > 0
                          ? `$${formatCurrency(total)}`
                          : '-'}
                      </td>
                      <td className={styles.itemActions}>
                        {productEntries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveProductEntry(entry.id)}
                            className={styles.removeEntryButton}
                            aria-label="Remove entry"
                            disabled={isSubmitting}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={styles.addButtonContainer}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddProductEntry}
                className={styles.addEntryButton}
                disabled={isSubmitting}
              >
                <Plus size={16} />
                Add Product
              </Button>
            </div>
          </div>

          {/* Payment Methods Table */}
          <div className={styles.paymentSection}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Payment Methods</h3>
            </div>
            <table className={styles.itemsTable}>
              <thead>
                <tr>
                  <th className={styles.itemHeader}>#</th>
                  <th className={styles.itemHeader}>Payment Method</th>
                  <th className={styles.itemHeaderAmount}>Amount</th>
                  <th className={styles.itemHeaderActions}></th>
                </tr>
              </thead>
              <tbody>
                {cashOrArEntries.map((entry, index) => {
                  return (
                    <tr key={entry.id} className={styles.itemRow}>
                      <td className={styles.itemNumber}>{index + 1}</td>
                      <td className={styles.itemDescription}>
                        <SearchInput<PaymentMethod>
                          items={paymentMethods}
                          value={
                            paymentMethods.find(
                              (pm) => pm.categoryId === entry.categoryId,
                            )?.id || ''
                          }
                          onChange={(paymentMethodId) => {
                            const selectedPm = paymentMethods.find(
                              (pm) => pm.id === paymentMethodId,
                            );
                            const categoryId = selectedPm
                              ? selectedPm.categoryId
                              : '';
                            handleCashOrArEntryChange(
                              entry.id,
                              'categoryId',
                              categoryId,
                            );
                          }}
                          getDisplayValue={(pm) => pm.alias}
                          placeholder="Search for a payment method..."
                          required
                          disabled={isSubmitting}
                        />
                      </td>
                      <td className={styles.itemAmount}>
                        {needsSplit ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={
                              entry.amount !== undefined &&
                              entry.amount !== null
                                ? typeof entry.amount === 'string'
                                  ? entry.amount
                                  : entry.amount.toString()
                                : ''
                            }
                            placeholder="0.00"
                            className={styles.numberInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (
                                value === '' ||
                                /^\d*\.?\d{0,2}$/.test(value)
                              ) {
                                handleCashOrArEntryChange(
                                  entry.id,
                                  'amount',
                                  value === '' ? undefined : value,
                                );
                              }
                            }}
                            disabled={isSubmitting}
                          />
                        ) : (
                          <div className={styles.autoComputedAmount}>
                            ${formatCurrency(totalRevenue)}
                          </div>
                        )}
                      </td>
                      <td className={styles.itemActions}>
                        {cashOrArEntries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCashOrArEntry(entry.id)}
                            className={styles.removeEntryButton}
                            aria-label="Remove entry"
                            disabled={isSubmitting}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={styles.addButtonContainer}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddCashOrArEntry}
                className={styles.addEntryButton}
                disabled={isSubmitting}
              >
                <Plus size={16} />
                {!needsSplit ? 'Split Payment' : 'Add Payment Method'}
              </Button>
            </div>
          </div>

          {/* Totals Section */}
          <div className={styles.totalsSection}>
            <div className={styles.totalsTable}>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Subtotal:</span>
                <span className={styles.totalValue}>
                  ${formatCurrency(totalRevenue)}
                </span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Total:</span>
                <span className={styles.totalValue}>
                  ${formatCurrency(totalRevenue)}
                </span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Amount Paid:</span>
                <span
                  className={`${styles.totalValue} ${
                    balanceDifference > 0.01 ? styles.totalsDifference : ''
                  }`}
                >
                  ${formatCurrency(totalCashOrAr)}
                </span>
              </div>
            </div>
            {balanceDifference > 0.01 && (
              <p className={styles.balanceWarning}>
                {totalCashOrAr > totalRevenue
                  ? `Payment is over by ${formatCurrency(balanceDifference)}`
                  : `Payment is short by ${formatCurrency(balanceDifference)}`}
              </p>
            )}
          </div>

          {/* Description */}
          <div className={styles.descriptionSection}>
            <label htmlFor="description" className={styles.label}>
              Description (Optional)
            </label>
            <textarea
              id="description"
              {...register('description')}
              className={styles.textarea}
              rows={3}
              placeholder="Enter a description for this sale..."
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formFooter}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(ROUTES.SALES.ROOT)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={balanceDifference > 0.01 || isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Sales Transaction'}
            </Button>
          </div>
        </form>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default SalesTransaction;
